import React, { useState, useRef, useEffect } from 'react';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Copy, Bold, Plus, Minus, Edit2, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { useCanvas } from '../context/CanvasContext';
import 'react-resizable/css/styles.css';

const TextBlock = ({ block }) => {
  const { mode, updateBlock, removeBlock, addBlock, snapToGrid, zoom, selectedBlockId, setSelectedBlockId } = useCanvas();
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const isSelected = selectedBlockId === block.id;

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
    }, 600); // Increased delay
  };

  useEffect(() => {
    return () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
    };
  }, []);

  // Default to edit mode if text is empty (newly created), otherwise view mode
  const [isEditing, setIsEditing] = useState(!block.data.text);
  const [localText, setLocalText] = useState(block.data.text || '');
  const nodeRef = useRef(null);
  const [position, setPosition] = useState(block.position);

  useEffect(() => {
    setPosition(block.position);
  }, [block.position]);

  useEffect(() => {
    setLocalText(block.data.text || '');
  }, [block.data.text]);

  const handleDragStop = (e, data) => {
    const newPos = { x: data.x, y: data.y };
    setPosition(newPos);
    updateBlock(block.id, { position: newPos });
  };

  const handleResize = (e, { size }) => {
    updateBlock(block.id, { size });
  };

  const handleDuplicate = () => {
    addBlock('text', { ...block.data }, { x: position.x + 20, y: position.y + 20 });
  };

  const handleTextChange = (e) => {
    setLocalText(e.target.value);
  };

  const toggleEdit = () => {
      setIsEditing(!isEditing);
  };

  const handleBlur = () => {
      // When clicking outside or finishing edit, go back to view mode
      setIsEditing(false);
      if (localText !== block.data.text) {
          updateBlock(block.id, { data: { ...block.data, text: localText } });
      }
  }

  const toggleBold = () => {
    updateBlock(block.id, { data: { ...block.data, isBold: !block.data.isBold } });
  };

  const changeFontSize = (delta) => {
    const currentSize = block.data.fontSize || 16;
    updateBlock(block.id, { data: { ...block.data, fontSize: Math.max(8, currentSize + delta) } });
  };

  const handleAlign = (align) => {
    updateBlock(block.id, { data: { ...block.data, textAlign: align } });
  };

  const content = (
    <div
      className="text-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={(e) => {
        // Select on click/touch
        setSelectedBlockId(block.id);
      }}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        color: 'var(--text-color)',
        padding: '8px',
        border: isSelected ? '2px solid var(--text-color)' : 'none', // Add border for selection
        borderRadius: '4px'
      }}
    >
       <AnimatePresence>
          {mode === 'edit' && (isHovered || isSelected) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{
                position: 'absolute',
                top: '-35px',
                left: '50%',
                x: '-50%',
                scale: 1 / zoom,
                transformOrigin: 'bottom center',
                display: 'flex',
                gap: '8px',
                background: 'var(--bg-color)',
                padding: '4px 8px',
                borderRadius: '20px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 1000,
                border: '1px solid var(--border-color)'
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button className="icon-btn" onClick={toggleEdit} title="Edit Text" style={{ background: isEditing ? 'rgba(0,0,0,0.1)' : 'transparent' }}><Edit2 size={12} /></button>
              <button className="icon-btn" onClick={toggleBold} title="Toggle Bold" style={{ fontWeight: block.data.isBold ? 'bold' : 'normal', background: block.data.isBold ? 'rgba(0,0,0,0.1)' : 'transparent' }}><Bold size={12} /></button>
              
              <div style={{ width: '1px', background: 'var(--border-color)', margin: '0 4px' }}></div>
              
              <button className="icon-btn" onClick={() => handleAlign('left')} title="Align Left" style={{ background: block.data.textAlign === 'left' ? 'rgba(0,0,0,0.1)' : 'transparent' }}><AlignLeft size={12} /></button>
              <button className="icon-btn" onClick={() => handleAlign('center')} title="Align Center" style={{ background: block.data.textAlign === 'center' ? 'rgba(0,0,0,0.1)' : 'transparent' }}><AlignCenter size={12} /></button>
              <button className="icon-btn" onClick={() => handleAlign('right')} title="Align Right" style={{ background: block.data.textAlign === 'right' ? 'rgba(0,0,0,0.1)' : 'transparent' }}><AlignRight size={12} /></button>
              
              <div style={{ width: '1px', background: 'var(--border-color)', margin: '0 4px' }}></div>

              <button className="icon-btn" onClick={() => changeFontSize(-2)} title="Decrease Font Size"><Minus size={12} /></button>
              <button className="icon-btn" onClick={() => changeFontSize(2)} title="Increase Font Size"><Plus size={12} /></button>
              <div style={{ width: '1px', background: 'var(--border-color)', margin: '0 4px' }}></div>
              <button className="icon-btn" onClick={handleDuplicate} title="Duplicate"><Copy size={12} /></button>
              <button className="icon-btn" onClick={() => removeBlock(block.id)} style={{ color: 'red' }} title="Delete"><Trash2 size={12} /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {mode === 'edit' && isEditing ? (
            <textarea
                value={localText}
                onChange={handleTextChange}
                placeholder="Type something..."
                autoFocus
                onBlur={handleBlur}
                className="text-input-editing"
                style={{
                    width: '100%',
                    height: '100%',
                    background: 'transparent',
                    border: '1px dashed var(--border-color)',
                    borderRadius: '4px',
                    resize: 'none',
                    fontFamily: 'inherit',
                    fontSize: (block.data.fontSize || 16) + 'px',
                    fontWeight: block.data.isBold ? 'bold' : 'normal',
                    textAlign: block.data.textAlign || 'left',
                    color: 'inherit',
                    outline: 'none',
                    lineHeight: '1.5',
                    padding: '8px'
                }}
            />
        ) : (
            <div style={{ 
                whiteSpace: 'pre-wrap', 
                fontSize: (block.data.fontSize || 16) + 'px', 
                fontWeight: block.data.isBold ? 'bold' : 'normal', 
                textAlign: block.data.textAlign || 'left',
                lineHeight: '1.5',
                width: '100%',
                height: '100%',
                padding: '8px',
                cursor: mode === 'edit' ? 'move' : 'default',
                border: mode === 'edit' && isHovered && !isSelected ? '1px dashed var(--border-color)' : '1px solid transparent'
            }}>
                {block.data.text || ''}
            </div>
        )}
    </div>
  );

  if (mode === 'view') {
    return (
        <div
        style={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: block.size.width,
          height: block.size.height,
          zIndex: block.zIndex,
          pointerEvents: 'none'
        }}
      >
          <div style={{ 
              whiteSpace: 'pre-wrap', 
              fontSize: (block.data.fontSize || 16) + 'px', 
              fontWeight: block.data.isBold ? 'bold' : 'normal', 
              textAlign: block.data.textAlign || 'left',
              color: 'var(--text-color)', 
              padding: '8px', 
              lineHeight: '1.5' 
            }}>
                {block.data.text || ''}
            </div>
      </div>
    );
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      position={position}
      onStop={handleDragStop}
      grid={snapToGrid ? [20, 20] : null}
      scale={zoom}
      cancel="textarea, .icon-btn, .react-resizable-handle"
    >
      <div
        ref={nodeRef}
        style={{
          position: 'absolute',
          zIndex: isHovered ? 999999 : block.zIndex,
        }}
      >
        <ResizableBox
          width={block.size.width}
          height={block.size.height}
          onResizeStop={handleResize}
          minConstraints={[100, 50]}
          maxConstraints={[800, 800]}
          resizeHandles={['se']}
          handle={
             <span className="react-resizable-handle react-resizable-handle-se" style={{ opacity: isHovered ? 1 : 0 }} />
          }
        >
          {content}
        </ResizableBox>
      </div>
    </Draggable>
  );
};

export default TextBlock;
